import { useState } from 'react';
import Image from 'next/image';

import { Box, Button, FieldSet, Heading, Legend, Paragraph, Tag, TextButton } from '@zillow/constellation-10';

import { ApplicationSampleModal } from '@zg-rentals/applications-components';
import ApplicationsInfoModal from '../../modals/ApplicationsInfoModal';

import UserInfo from '../../../assets/user-info.svg';

import ApplicationsBenefitsContent from './ApplicationsBenefitsContent';
import { ApplicationsLabeledControl } from './ApplicationsLabeledControl';

import type { ChangeEvent } from 'react';

type Props = {
  isIntroModal?: boolean;
  isRoomForRent?: boolean;
  isCompact?: boolean;
  radioValue: string;
  onRadioChange: (event: ChangeEvent<Element>) => void;
  serverError?: string;
  disabled?: boolean;
};

const ApplicationsFormField = ({
  isIntroModal = false,
  isRoomForRent = false,
  isCompact = false,
  radioValue,
  onRadioChange,
  serverError,
  disabled = false,
}: Props) => {
  const [displaySampleModal, setDisplaySampleModal] = useState(false);
  const [displayInfoModal, setDisplayInfoModal] = useState(false);

  if (isCompact) {
    return (
      <>
        <FieldSet
          required={true}
          legend={
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Legend size="lg">Receive Zillow applications for this listing?</Legend>
              <Tag tone="info" css={{ marginInline: '200' }}>
                Free
              </Tag>
              <TextButton onClick={() => setDisplayInfoModal(true)}>Learn more</TextButton>
            </Box>
          }
          description={
            serverError && (
              <Paragraph css={{ color: 'text.feedback.critical.strong', marginBottom: '400' }}>{serverError}</Paragraph>
            )
          }
        >
          <Box css={{ display: 'flex', paddingLeft: '200', flexDirection: 'column' }}>
            <ApplicationsLabeledControl
              isApplicationsAccepted={radioValue}
              onChange={
                disabled
                  ? () => {
                      /* disabled */
                    }
                  : onRadioChange
              }
            />
          </Box>
        </FieldSet>

        {displayInfoModal && (
          <ApplicationsInfoModal onClose={() => setDisplayInfoModal(false)} isRoomForRent={isRoomForRent} />
        )}
      </>
    );
  }

  return (
    <>
      <Tag tone="info" css={{ marginRight: '200', marginBottom: '400' }}>
        Free
      </Tag>

      <Heading level={2} textStyle="heading-sm">
        {isIntroModal ? 'Receive Zillow applications' : 'Receive Zillow applications for this listing?'}
      </Heading>

      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '600' }}>
        <Box css={{ gridColumnStart: '1', gridColumnEnd: { base: 'span 12', xl: 'span 8' } }}>
          <ApplicationsBenefitsContent isIntroModal={isIntroModal} isRoomForRent={isRoomForRent} />
        </Box>

        <Box
          css={{
            display: 'grid',
            gridColumnStart: { base: '1', xl: '9' },
            gridColumnEnd: { base: 'span 12', xl: 'span 4' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Image alt="" src={UserInfo} />
            <Button tone="brand" emphasis="filled" onClick={() => setDisplaySampleModal(true)}>
              View sample
            </Button>
          </Box>
        </Box>

        <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 12' }}>
          {serverError && (
            <Paragraph css={{ color: 'text.feedback.critical.strong', marginBottom: '400' }}>{serverError}</Paragraph>
          )}

          <FieldSet required={true} legend={<Legend>Receive Zillow applications for this listing?</Legend>}>
            <Box
              css={{
                display: 'flex',
                paddingLeft: '200',
                flexDirection: 'column',
              }}
            >
              <ApplicationsLabeledControl
                isApplicationsAccepted={radioValue}
                onChange={
                  disabled
                    ? () => {
                        /* disabled */
                      }
                    : onRadioChange
                }
              />
            </Box>
          </FieldSet>
        </Box>
      </Box>

      {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
    </>
  );
};

export default ApplicationsFormField;
